/* eslint-disable react/no-danger */
/* eslint-disable import/no-extraneous-dependencies */
import { BellOutlined, CheckCircleOutlined, ClockCircleOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Empty, Input, List, Modal, Space, Tabs, Tag, Typography } from 'antd';
import {
  actionGetAnnouncements,
  IInfo,
  selectLoadingNotification,
  selectLoadingNotificationInfo,
} from 'components/Header/Notification/slice';
import { useEffect, useMemo, useState } from 'react';
import HTMLString from 'react-html-string';
import { useAppDispatch, useAppSelector } from 'redux/store';
import {
  getReadNotificationIds,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  markNotificationAsUnread,
} from 'util/notificationStorage';

const { Title, Text, Paragraph } = Typography;

export default function NotificationList() {
  const dispatch = useAppDispatch();
  const listNotification = useAppSelector(selectLoadingNotificationInfo);
  const statusLoad = useAppSelector(selectLoadingNotification);

  const [readIds, setReadIds] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'read'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotification, setSelectedNotification] = useState<IInfo | null>(null);

  useEffect(() => {
    dispatch(actionGetAnnouncements());
    setReadIds(getReadNotificationIds());
  }, [dispatch]);

  const validNotifications = useMemo(() => {
    if (!Array.isArray(listNotification)) return [];
    return listNotification.filter((item) => !item.hide && Boolean(item.description));
  }, [listNotification]);

  const unreadCount = useMemo(
    () => validNotifications.filter((item) => item.id && !readIds.includes(item.id)).length,
    [validNotifications, readIds]
  );

  const readCount = useMemo(
    () => validNotifications.filter((item) => item.id && readIds.includes(item.id)).length,
    [validNotifications, readIds]
  );

  const filteredNotifications = useMemo(
    () =>
      validNotifications.filter((item) => {
        const isRead = item.id ? readIds.includes(item.id) : true;

        // Filter by tab
        if (activeTab === 'unread' && isRead) return false;
        if (activeTab === 'read' && !isRead) return false;

        // Filter by search
        if (searchTerm.trim()) {
          const query = searchTerm.toLowerCase();
          const matchTitle = item.description?.toLowerCase().includes(query);
          const matchContent = item.content?.toLowerCase().includes(query);
          return matchTitle || matchContent;
        }

        return true;
      }),
    [validNotifications, readIds, activeTab, searchTerm]
  );

  const handleMarkAllRead = () => {
    const allIds = validNotifications.map((item) => item.id).filter(Boolean) as number[];
    const updated = markAllNotificationsAsRead(allIds);
    setReadIds(updated);
  };

  const handleItemClick = (item: IInfo) => {
    if (item.id) {
      const updated = markNotificationAsRead(item.id);
      setReadIds(updated);
    }
    setSelectedNotification(item);
  };

  const handleToggleReadStatus = (e: React.MouseEvent, id?: number) => {
    e.stopPropagation();
    if (!id) return;
    if (readIds.includes(id)) {
      const updated = markNotificationAsUnread(id);
      setReadIds(updated);
    } else {
      const updated = markNotificationAsRead(id);
      setReadIds(updated);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 mb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 rounded-2xl p-6 sm:p-8 text-white shadow-lg mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur-sm">
              <BellOutlined className="text-2xl text-white" />
            </div>
            <div>
              <Title level={3} style={{ color: '#fff', margin: 0 }}>
                Trung tâm Thông báo
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.85)' }} className="text-sm">
                Cập nhật các thông tin, tính năng mới và thông báo hệ thống quan trọng
              </Text>
            </div>
          </div>
        </div>

        {unreadCount > 0 && (
          <Button
            type="primary"
            ghost
            icon={<CheckCircleOutlined />}
            onClick={handleMarkAllRead}
            className="border-white text-white hover:!bg-white/20 hover:!border-white self-start sm:self-auto rounded-lg"
          >
            Đánh dấu tất cả đã đọc ({unreadCount})
          </Button>
        )}
      </div>

      {/* Content Card */}
      <Card className="shadow-sm border-gray-200 rounded-xl overflow-hidden">
        {/* Filters and Search Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-100 pb-4 mb-4">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as 'all' | 'unread' | 'read')}
            className="w-full sm:w-auto"
            items={[
              {
                key: 'all',
                label: (
                  <span>
                    Tất cả <Badge count={validNotifications.length} overflowCount={99} className="ml-1" />
                  </span>
                ),
              },
              {
                key: 'unread',
                label: (
                  <span>
                    Chưa đọc <Badge count={unreadCount} overflowCount={99} className="ml-1" />
                  </span>
                ),
              },
              {
                key: 'read',
                label: (
                  <span>
                    Đã đọc{' '}
                    <Badge
                      count={readCount}
                      overflowCount={99}
                      style={{ backgroundColor: '#52c41a' }}
                      className="ml-1"
                    />
                  </span>
                ),
              },
            ]}
          />

          <Input
            placeholder="Tìm kiếm thông báo..."
            prefix={<SearchOutlined className="text-gray-400" />}
            allowClear
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64 rounded-lg"
          />
        </div>

        {/* Notifications List */}
        <List
          loading={statusLoad === 'loading'}
          itemLayout="vertical"
          pagination={{
            pageSize: 6,
            align: 'center',
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
          dataSource={filteredNotifications}
          locale={{
            emptyText: (
              <div className="py-12">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={(() => {
                    if (activeTab === 'unread') return 'Bạn đã đọc hết tất cả thông báo!';
                    if (searchTerm) return 'Không tìm thấy thông báo phù hợp.';
                    return 'Hiện chưa có thông báo nào.';
                  })()}
                />
              </div>
            ),
          }}
          renderItem={(item) => {
            const isRead = item.id ? readIds.includes(item.id) : true;
            return (
              <div
                key={item.id || item.description}
                role="button"
                tabIndex={0}
                onClick={() => handleItemClick(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleItemClick(item);
                  }
                }}
                className={`mb-3 p-4 sm:p-5 rounded-xl border transition-all duration-200 cursor-pointer ${
                  isRead
                    ? 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                    : 'border-l-4 border-l-blue-500 border-gray-200 bg-blue-50/20 hover:bg-blue-50/40 hover:shadow-sm'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!isRead && (
                      <Tag color="processing" className="rounded-full font-medium">
                        Mới
                      </Tag>
                    )}
                    <span className={`text-base font-semibold ${isRead ? 'text-gray-800' : 'text-blue-700'}`}>
                      {item.description}
                    </span>
                    {item.site_name && item.site_name !== 'all' && (
                      <Tag color="cyan" className="rounded">
                        {item.site_name}
                      </Tag>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    {item.created && (
                      <span className="flex items-center gap-1">
                        <ClockCircleOutlined />
                        {formatDate(item.created)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Content snippet */}
                <Paragraph ellipsis={{ rows: 2 }} className="text-sm text-gray-600 mb-3">
                  <HTMLString html={item.content} />
                </Paragraph>

                {/* Actions bottom */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    className="p-0 text-xs font-medium text-blue-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item);
                    }}
                  >
                    Xem chi tiết
                  </Button>

                  {item.id && (
                    <Button
                      type="text"
                      size="small"
                      className="text-xs text-gray-400 hover:text-gray-600"
                      onClick={(e) => handleToggleReadStatus(e, item.id)}
                    >
                      {isRead ? 'Đánh dấu chưa đọc' : 'Đánh dấu đã đọc'}
                    </Button>
                  )}
                </div>
              </div>
            );
          }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={<span className="text-lg font-bold text-blue-600">{selectedNotification?.description}</span>}
        open={Boolean(selectedNotification)}
        onOk={() => setSelectedNotification(null)}
        onCancel={() => setSelectedNotification(null)}
        width={700}
        footer={[
          <Button key="close" type="primary" onClick={() => setSelectedNotification(null)}>
            Đóng
          </Button>,
        ]}
      >
        <div className="py-3">
          <div className="flex items-center gap-2 mb-4 text-xs text-gray-400 border-b pb-3">
            {selectedNotification?.created && (
              <Space>
                <ClockCircleOutlined />
                <span>Thời gian: {formatDate(selectedNotification.created)}</span>
              </Space>
            )}
            {selectedNotification?.site_name && (
              <Tag color="blue" className="ml-2">
                Trang: {selectedNotification.site_name === 'all' ? 'Tất cả' : selectedNotification.site_name}
              </Tag>
            )}
          </div>

          <div className="prose max-w-none text-base text-gray-800 leading-relaxed">
            <HTMLString html={selectedNotification?.content || 'Không có nội dung chi tiết'} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
